import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router";
import { App as AntdApp, Alert, Form, Input, InputNumber, DatePicker, Select, Button, Card, Row, Col, Modal, Segmented, message, Switch, Space, Divider, Table, Breadcrumb, Tag } from "antd";
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, HomeOutlined, AuditOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelect } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { usePageTitle } from "../../hooks";
import { VarietySelect } from "../../components/common/VarietySelect";
import "leaflet/dist/leaflet.css";
import dayjs from "dayjs";
import type { AuditPlot, AuditPlotFormValues, AuditPlotPlanting } from "../../types/audit";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";
import { useGetIdentity } from "@refinedev/core";
import { auditPlotService } from "../../services/api";
import { PlotMapEditor } from "./components/PlotMapEditor";
import { PlantingCardInline } from "./components/PlantingCardInline";
import { SubGeomEditorModal } from "./components/SubGeomEditorModal";
import { useAuditPlotPlantings } from "@/hooks/useAuditPlotPlantings";
import { computeGeodesicAreaHa } from "@/utils/geo";

/** Accent + case insensitive filter for antd Selects whose options
 *  come from `useSelect` as `{label, value}` pairs. Typing "limon"
 *  matches "LIMÓN" / "Limonero" / "limôn". */
const accentInsensitiveFilter = (
  input: string,
  option?: { label?: unknown },
) => {
  const norm = (s: unknown) =>
    String(s ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");
  return norm(option?.label).includes(norm(input));
};

interface ProductionBlock {
  key: string;
  percentage: number;
  avg_yield: number;
}

/**
 * AuditPlotForm Component
 * Page for creating or editing audit plots with map integration
 */
export const AuditPlotForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: auditId, plotId } = useParams<{ id: string; plotId?: string }>();

  // Geometry pre-loaded from the audit-detail map's draw flow. The
  // user drew the polygon there and we navigated here with it in
  // router state — we only consume it on first mount of the create
  // form, not in edit mode (the edit flow loads its own geom).
  const initialGeometryFromRoute = (location.state as any)
    ?.initialGeometry as GeoJSON.Geometry | undefined;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [plot, setPlot] = useState<AuditPlot | null>(null);
  // Edición de plantings desde web disponible para admin (user_type
  // 1, 2) y coordinador (4). Auditor puro (5) edita desde el móvil.
  const { data: identity } = useGetIdentity<UserInfo>();
  const canEditPlantings = (() => {
    const t = identity?.app_user?.user_type;
    if (t == null) return false;
    return (ADMIN_USER_TYPES as readonly number[]).includes(t) || t === 4;
  })();
  const { modal } = AntdApp.useApp();
  // Fase 4 refactor de doblados: hook CRUD reactivo de plantings (web).
  // Sólo tiene sentido con parcela ya persistida — en modo creación
  // ocultamos la sección hasta el primer guardado.
  // syncPrimaryFromParent está disponible en el hook pero ya no se
  // llama desde web: el ViewSet lo hace en perform_create/update
  // (Fase B). Lo dejamos exportado para poder mockearlo en tests.
  const {
    plantings,
    addPlanting,
    updatePlanting,
    removePlanting,
    refresh: refreshPlantings,
  } = useAuditPlotPlantings(plotId ?? null);
  const extraPlantings = useMemo(
    () => plantings.filter((p) => p.ordering !== 1),
    [plantings],
  );
  const [isDoubled, setIsDoubled] = useState<boolean>(false);
  // Planting cuyo sub-área está en edición — abre el SubGeomEditorModal.
  const [subGeomTarget, setSubGeomTarget] = useState<AuditPlotPlanting | null>(
    null,
  );
  // Modo Total/Parcial inferido: si al menos una extra tiene sub_geom
  // → parcial; si ninguna → total. El usuario puede forzar el modo con
  // el toggle (al pasar a Total borramos los sub_geom con confirmación).
  const inferredMode: "total" | "partial" = extraPlantings.some(
    (p) => p.sub_geom != null,
  )
    ? "partial"
    : "total";
  const [doublingMode, setDoublingMode] = useState<"total" | "partial">(
    inferredMode,
  );
  // Cuando el hook trae plantings extra, sincronizamos el toggle y el
  // modo con la realidad (evita quedarse en "No doblada" tras un
  // refetch que trae extras del backend).
  useEffect(() => {
    if (extraPlantings.length > 0) setIsDoubled(true);
    setDoublingMode(inferredMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraPlantings.length, inferredMode]);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(
    initialGeometryFromRoute ?? null,
  );
  const [productionBlocks, setProductionBlocks] = useState<ProductionBlock[]>([]);
  const [estimatedProduction, setEstimatedProduction] = useState<number>(0);
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [auditReference, setAuditReference] = useState<string>("");

  const isEditMode = !!plotId;
  
  // Set page title
  usePageTitle(isEditMode ? t("plot.edit_title", "Editar Parcela") : t("plot.create_title", "Crear Parcela"));

  // Catalogue selectors. Refine's useSelect defaults to the data
  // provider's default page size (15) which cut off these lists; the
  // catalogues here are short enough that fetching 500 in one shot
  // is the simplest fix and matches the pattern used elsewhere in
  // the app (e.g. producers/create.tsx).
  const { selectProps: patternSelectProps } = useSelect({
    resource: "patterns",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });

  const { selectProps: plotTypeSelectProps } = useSelect({
    resource: "plot-types",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });

  const { selectProps: intermediateWoodSelectProps } = useSelect({
    resource: "intermediate-woods",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });

  // Catálogos crudos para `PlantingCardInline` (necesita `{id, name, code?}`
  // directo, no `{label, value}` de useSelect). Fetch en paralelo con
  // los selects — Refine dedupe la request si tienen la misma key.
  const { result: varietiesRes } = useList<{ id: number; name: string; code?: string }>({
    resource: "varieties",
    pagination: { pageSize: 500 },
  });
  const { result: patternsRes } = useList<{ id: number; name: string }>({
    resource: "patterns",
    pagination: { pageSize: 500 },
  });
  const { result: plotTypesRes } = useList<{ id: number; name: string }>({
    resource: "plot-types",
    pagination: { pageSize: 500 },
  });
  const { result: intermediatesRes } = useList<{ id: number; name: string }>({
    resource: "intermediate-woods",
    pagination: { pageSize: 500 },
  });
  const varietyOptions = varietiesRes?.data ?? [];
  const patternOptions = patternsRes?.data ?? [];
  const plotTypeOptions = plotTypesRes?.data ?? [];
  const intermediateOptions = intermediatesRes?.data ?? [];

  // Calculate estimated production when blocks or plants change
  useEffect(() => {
    calculateEstimatedProduction();
  }, [productionBlocks, form.getFieldValue('plants')]);

  // Watched form fields used by the auto-calculations below. The
  // surface field is the parcel's surface in hectares, marco_row /
  // marco_column are planting frame in metres.
  const watchedSurface = Form.useWatch("surface", form);
  const watchedRow = Form.useWatch("plantation_frame_row", form);
  const watchedColumn = Form.useWatch("plantation_frame_column", form);

  const polygonAreaHa = useMemo(
    () => computeGeodesicAreaHa(geometry),
    [geometry],
  );

  // Auto-compute the number of plants from surface + marco. Formula:
  //   plants = surface_ha * 10000 / (row_m * column_m)
  // Runs whenever any of the three changes; the user can still edit
  // the result manually afterwards (it'll just be overwritten if
  // they tweak surface/marco again — accepted tradeoff for "set it
  // and forget it" behaviour).
  useEffect(() => {
    const s = Number(watchedSurface);
    const r = Number(watchedRow);
    const c = Number(watchedColumn);
    if (!s || !r || !c) return;
    const plants = Math.round((s * 10000) / (r * c));
    if (Number.isFinite(plants) && plants > 0) {
      form.setFieldValue("plants", plants);
    }
  }, [watchedSurface, watchedRow, watchedColumn, form]);

  // Load plot data if editing
  useEffect(() => {
    if (isEditMode && plotId) {
      loadPlotData();
    }
  }, [plotId, isEditMode]);

  // Fetch audit reference for breadcrumb
  useEffect(() => {
    const fetchAuditReference = async () => {
      if (!auditId) return;

      try {
        const { apiClient } = await import("../../services/api/apiClient");
        const response = await apiClient.get(`/audits/${auditId}/`);
        setAuditReference(response.data.reference || auditId);
      } catch (error) {
        console.error("Error fetching audit reference:", error);
        setAuditReference(auditId);
      }
    };

    fetchAuditReference();
  }, [auditId]);

  // Fetch the audit's farm reference geometry so the map opens
  // already centred on the farm — the user shouldn't have to pan
  // halfway across the world to start drawing the new parcel.
  useEffect(() => {
    const fetchFarmReference = async () => {
      if (!auditId) return;
      try {
        const { apiClient } = await import("../../services/api/apiClient");
        const res = await apiClient.get(`/audits/${auditId}/map_data/`);
        const features = Array.isArray(res.data?.features)
          ? res.data.features
          : [];
        const farmFeatures = features.filter(
          (f: any) => f?.properties?.layer_type === "farm_plot",
        );
        if (farmFeatures.length === 0) {
          setReferencePlots(null);
          return;
        }
        setReferencePlots({
          type: "FeatureCollection",
          features: farmFeatures,
        } as GeoJSON.FeatureCollection);
      } catch (error) {
        console.error("Error loading farm reference geometry:", error);
        setReferencePlots(null);
      }
    };

    fetchFarmReference();
  }, [auditId]);

  // Handlers de los toggles "Es doblada" y "Total/Parcial" — mismo
  // comportamiento que el wizard móvil (PlotDataStep.tsx).
  const handleToggleDoubled = async (nextValue: boolean) => {
    if (!plotId) {
      // En modo creación no podemos crear plantings sin plot; el
      // toggle sólo cambia el estado local hasta el primer guardado.
      setIsDoubled(nextValue);
      return;
    }
    if (!nextValue && extraPlantings.length > 0) {
      modal.confirm({
        title: "¿Desactivar parcela doblada?",
        content: `Se eliminarán las ${extraPlantings.length} plantaciones extra. La plantación principal se mantiene.`,
        okText: "Desactivar",
        okButtonProps: { danger: true },
        cancelText: "Cancelar",
        onOk: async () => {
          for (const p of extraPlantings) {
            await removePlanting(p.id);
          }
          setIsDoubled(false);
        },
      });
      return;
    }
    setIsDoubled(nextValue);
  };

  const handleToggleDoublingMode = (nextMode: "total" | "partial") => {
    if (nextMode === doublingMode) return;
    if (nextMode === "total") {
      const withSubGeom = extraPlantings.filter((p) => p.sub_geom != null);
      if (withSubGeom.length > 0) {
        modal.confirm({
          title: "Cambiar a doblado total",
          content: `Las ${withSubGeom.length} sub-áreas dibujadas se perderán. ¿Continuar?`,
          okText: "Cambiar",
          okButtonProps: { danger: true },
          cancelText: "Cancelar",
          onOk: async () => {
            for (const p of withSubGeom) {
              await updatePlanting(p.id, { sub_geom: null, surface: null });
            }
            setDoublingMode("total");
          },
        });
        return;
      }
    }
    setDoublingMode(nextMode);
  };

  const handleAddPlanting = async () => {
    if (!plotId) {
      message.warning(
        "Guarda la parcela primero para poder añadir plantaciones dobladas.",
      );
      return;
    }
    try {
      // Precargamos variedad/patrón/tipo del padre — en dobladas es
      // lo más habitual y evita hacer clic 3 veces por card. El
      // auditor puede sobreescribir cada campo en la card.
      await addPlanting({
        variety: form.getFieldValue("variety") ?? null,
        pattern: form.getFieldValue("pattern") ?? null,
        plot_type: form.getFieldValue("plot_type") ?? null,
      });
      setIsDoubled(true);
    } catch (err) {
      const detail =
        (err as { response?: { data?: unknown } }).response?.data ??
        (err as Error).message;
      message.error(`No se pudo añadir: ${JSON.stringify(detail)}`);
    }
  };

  // Nº plantas del padre a repartir en modo total: cuando la parcela
  // está doblada en modo total, el `plants` del padre representa el
  // total de la parcela y cada planting recibe total / N sugerido.
  // Al no estar doblada o en parcial, la planting hereda tal cual el
  // valor del padre (usado sólo como referencia — en parcial la
  // sugerencia real viene del sub_geom).
  const parentPlantsForCards = useMemo(() => {
    const raw = form.getFieldValue("plants") as number | null | undefined;
    if (raw == null) return null;
    if (!isDoubled || doublingMode === "partial") return raw;
    const n = Math.max(plantings.length, 2);
    return Math.round(raw / n);
    // La dependencia de plants se toma vía watchedSurface/Row/Col
    // arriba: al cambiar plantas se re-renderiza y este useMemo se
    // recalcula porque leemos form.getFieldValue en el body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoubled, doublingMode, plantings.length, watchedSurface, watchedRow, watchedColumn]);

  const calculateEstimatedProduction = () => {
    const plants = form.getFieldValue('plants') || 0;
    
    if (plants === 0 || productionBlocks.length === 0) {
      setEstimatedProduction(0);
      return;
    }

    // Formula: (Σ(percentage * avg_yield) / 100) * total_plants
    const totalYield = productionBlocks.reduce((sum, block) => {
      return sum + (block.percentage * block.avg_yield);
    }, 0);

    const estimated = (totalYield / 100) * plants;
    setEstimatedProduction(estimated);
  };

  const addProductionBlock = () => {
    const newBlock: ProductionBlock = {
      key: Date.now().toString(),
      percentage: 0,
      avg_yield: 0,
    };
    setProductionBlocks([...productionBlocks, newBlock]);
  };

  const removeProductionBlock = (key: string) => {
    setProductionBlocks(productionBlocks.filter(block => block.key !== key));
  };

  const updateProductionBlock = (key: string, field: keyof ProductionBlock, value: number) => {
    // If updating percentage, validate that total doesn't exceed 100
    if (field === 'percentage') {
      const otherBlocksTotal = productionBlocks
        .filter(block => block.key !== key)
        .reduce((sum, block) => sum + block.percentage, 0);
      
      if (otherBlocksTotal + value > 100) {
        message.warning(t("plot.percentage_exceeded") || "La suma de los porcentajes no puede superar el 100%");
        return;
      }
    }

    setProductionBlocks(productionBlocks.map(block => 
      block.key === key ? { ...block, [field]: value } : block
    ));
  };

  const loadPlotData = async () => {
    try {
      setLoading(true);
      
      const response = await auditPlotService.getPlot(plotId!);
      
      // GeoFeatureModelSerializer returns data in GeoJSON format
      // Extract properties from the GeoJSON response
      const data = (response as any).properties || response;
      
      setPlot(data as any);
      
      // Load geometry separately
      try {
        const geometryData = await auditPlotService.getPlotGeometry(plotId!);

        if (geometryData.geom) {
          const parsedGeom = typeof geometryData.geom === 'string' 
            ? JSON.parse(geometryData.geom) 
            : geometryData.geom;
          setGeometry(parsedGeom);
          
          if (geometryData.source === 'similar_plot') {
            message.info(
              t("plot.geometry_from_similar") || 
              `Geometría cargada de otra parcela con el mismo nombre (ID: ${geometryData.source_plot_id})`
            );
          }
        } else {
          setGeometry(null);
        }
      } catch (geoError) {
        // Continue without geometry
        setGeometry(null);
      }

      // Load reference plots from the farm
      try {
        const refPlots = await auditPlotService.getReferencePlots(plotId!);
        setReferencePlots(refPlots as any);
      } catch (refError) {
        // Continue without reference plots
        setReferencePlots(null);
      }

      // Populate form
      form.setFieldsValue({
        name: data.name,
        plot_type: data.plot_type,
        pattern: data.pattern,
        surface: data.surface,
        plantation_date: data.plantation_date ? dayjs(data.plantation_date) : null,
        plantation_frame_row: data.plantation_frame_row,
        plantation_frame_column: data.plantation_frame_column,
        plants: data.plants,
        grafting: data.grafting,
        intermediate_wood: data.intermediate_wood,
        doubled_with: data.doubled_with,
        is_ilegal: data.is_ilegal || false,
        // PR3 — Variedad. Si la AuditPlot está precargada con FK
        // poblado, lo respetamos; si no, el backend rellena el
        // default con `plot_real.variety_fk` al crear.
        variety: (data as any).variety ?? null,
        // Calibre medio plot-level (mm). Backend lo devuelve como
        // `avg_caliber_calculated` — mismo campo, nombre engañoso
        // heredado. Ver types/audit.ts.
        avg_caliber:
          (data as any).avg_caliber_calculated != null
            ? (data as any).avg_caliber_calculated
            : undefined,
      });

      // Load production blocks if they exist
      if (data.blocks && data.blocks.length > 0) {
        const blocks = data.blocks.map((block: any, index: number) => ({
          key: `${Date.now()}-${index}`,
          percentage: block.percentage || 0,
          avg_yield: block.avg_yield || 0,
        }));
        setProductionBlocks(blocks);
      }

      // Set estimated production if available
      if (data.kg_plot) {
        setEstimatedProduction(data.kg_plot);
      }

      // Refetch plantings desde el hook — el AuditPlotSerializer los
      // trae nested pero el hook mantiene su propio estado y así lo
      // sincronizamos con el último snapshot del backend.
      await refreshPlantings();
    } catch (error: any) {
      message.error(error.message || t("error.plot.fetch_failed") || "Error al cargar los datos de la parcela");
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!auditId) {
      message.error("Audit ID is required");
      return;
    }

    // Validate that total percentage doesn't exceed 100%
    if (productionBlocks.length > 0) {
      const totalPercentage = productionBlocks.reduce((sum, block) => sum + block.percentage, 0);
      if (totalPercentage > 100) {
        message.error(t("plot.percentage_exceeded") || "La suma de los porcentajes no puede superar el 100%");
        return;
      }
    }

    try {
      setLoading(true);

      const formData: AuditPlotFormValues = {
        name: values.name,
        plot_type: values.plot_type,
        pattern: values.pattern,
        surface: values.surface,
        plantation_date: values.plantation_date?.format("YYYY-MM-DD") || null,
        plantation_frame_row: values.plantation_frame_row,
        plantation_frame_column: values.plantation_frame_column,
        plants: values.plants,
        grafting: values.grafting || null,
        intermediate_wood: values.intermediate_wood || null,
        doubled_with: values.doubled_with || null,
        is_ilegal: values.is_ilegal || false,
        // PR3 — Variedad obligatoria.
        variety: values.variety ?? null,
        kg_plot: estimatedProduction,
        // Calibre plot-level → AuditPlot.avg_caliber_calculated.
        // Aunque el Form.Item usa `name="avg_caliber"` (nombre corto en
        // la UI), el backend serializer sólo acepta la clave real del
        // modelo. Sin este mapping, DRF descartaba el valor sin ruido.
        avg_caliber_calculated:
          values.avg_caliber != null && Number.isFinite(values.avg_caliber)
            ? Number(values.avg_caliber)
            : 0,
        blocks: productionBlocks.map(block => ({
          percentage: block.percentage,
          avg_yield: block.avg_yield,
          // Legacy field — la UI ya no lo captura pero el modelo
          // sigue teniendo la columna NOT NULL. Enviamos 0 explícito.
          avg_caliber: 0,
        })),
        geom: geometry || undefined,
        // Backend's AuditPlotViewSet.create reads `audit` from the
        // request body (see backend/apps/audits_app/views/audit_plot_viewset.py).
        audit: parseInt(auditId),
      };

      let savedPlotId = plotId;
      if (isEditMode && plotId) {
        await auditPlotService.updatePlot(plotId, formData);
        message.success(t("plot.update_success") || "Parcela actualizada correctamente");
      } else {
        const created = await auditPlotService.createPlot(formData);
        savedPlotId = (created as any)?.id ?? (created as any)?.properties?.id;
        message.success(t("plot.create_success") || "Parcela creada correctamente");
      }

      // Refactor doblados Fase B: la sincronización de la planting
      // principal (ordering=1) con los campos legacy del padre la
      // hace ahora el backend en `perform_create` / `perform_update`
      // de AuditPlotViewSet. Antes se hacía aquí — al eliminarlo
      // evitamos un roundtrip extra por guardado y garantizamos que
      // la app móvil y el web mantengan la misma semántica.

      navigate(`/audits/${auditId}`);
    } catch (error: any) {
      message.error(error.message || t("error.plot.save_failed"));
    } finally {
      setLoading(false);
    }
  };

  // Custom breadcrumb
  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t("home.title", "Inicio")}
            </Link>
          ),
        },
        {
          title: (
            <Link to="/audits">
              <AuditOutlined /> {t("audit.title", "Auditorías")}
            </Link>
          ),
        },
        {
          title: <Link to={`/audits/${auditId}`}>{auditReference || auditId}</Link>,
        },
        {
          title: (
            <>
              <EnvironmentOutlined /> {isEditMode ? t("plot.edit_title", "Editar Parcela") : t("plot.create_title", "Crear Parcela")}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <Show
      isLoading={false}
      title={isEditMode ? t("plot.edit_title", "Editar Parcela") : t("plot.create_title", "Crear Parcela")}
      breadcrumb={breadcrumb}
      headerButtons={() => (
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/audits/${auditId}`)}
        >
          {t("common.back", "Volver")}
        </Button>
      )}
    >
      <Card loading={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Fila 1: Nombre Parcela / Tipo de Parcela */}
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("plot.name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("plot.name_required") || "El nombre es requerido",
                  },
                ]}
              >
                <Input placeholder={t("plot.name")} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("plot.plot_type")}
                name="plot_type"
                rules={[
                  {
                    required: true,
                    message: t("plot.plot_type_required") || "El tipo de parcela es requerido",
                  },
                ]}
              >
                <Select 
                  {...plotTypeSelectProps}
                  placeholder={t("plot.plot_type")}
                  showSearch
                  optionFilterProp="children"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* PR3 — Fila Variedad: por defecto hereda la variedad de
              la PlotReal precargada. El auditor sólo la cambia si en
              campo encuentra una variedad distinta a la declarada.
              Esa divergencia genera, en la PR4, una entrada en la
              bandeja "Confirmar variedad" del coordinador. */}
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("plot.variety", "Variedad protegida")}
                name="variety"
                rules={[
                  {
                    required: true,
                    message: t(
                      "plot.variety_required",
                      "La variedad es obligatoria",
                    ),
                  },
                ]}
                extra={t(
                  "plot.variety_hint",
                  "Se precarga desde la parcela declarada. Cámbiala si en campo encuentras una variedad distinta.",
                )}
              >
                <VarietySelect
                  placeholder={t("plot.variety", "Variedad protegida")}
                  allowClear={false}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Fila 2: Patrón / Fecha de Plantación */}
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("plot.rootstock")}
                name="pattern"
                rules={[
                  {
                    required: true,
                    message: t("plot.rootstock_required") || "El patrón es requerido",
                  },
                ]}
              >
                <Select
                  {...patternSelectProps}
                  placeholder={t("plot.rootstock")}
                  showSearch
                  // `useSelect` returns options as `{label, value}` —
                  // so we filter by `label`, not the JSX `children`.
                  // The custom filter is also accent/case-insensitive
                  // so typing "Garnem" or "GARNEM" both work.
                  optionFilterProp="label"
                  filterOption={accentInsensitiveFilter}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("plot.plantation_date")}
                name="plantation_date"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={t("plot.plantation_date")}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Fila 3: Marco fila / Marco columna / Superficie / Plantas.
              Orden pedido: primero el marco (que es lo que el auditor
              suele apuntar de memoria), luego superficie (con el hint
              del polígono) y, por último, plantas — que el sistema
              autocalcula a partir de los tres anteriores. */}
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={t("plot.plantation_frame_row")}
                name="plantation_frame_row"
                rules={[
                  {
                    required: true,
                    message: t("plot.plantation_frame_row_required") || "El marco de plantación (fila) es requerido",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.1}
                  placeholder={t("plot.plantation_frame_row")}
                  addonAfter="m"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={t("plot.plantation_frame_column")}
                name="plantation_frame_column"
                rules={[
                  {
                    required: true,
                    message: t("plot.plantation_frame_column_required") || "El marco de plantación (columna) es requerido",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.1}
                  placeholder={t("plot.plantation_frame_column")}
                  addonAfter="m"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={t("plot.surface")}
                name="surface"
                rules={[
                  {
                    required: true,
                    message: t("plot.surface_required") || "La superficie es requerida",
                  },
                ]}
                extra={
                  polygonAreaHa != null ? (
                    <span>
                      {t("plot.polygon_area_hint", "Polígono dibujado")}:{" "}
                      {polygonAreaHa.toFixed(2)} ha{" "}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, height: "auto" }}
                        onClick={() =>
                          form.setFieldValue(
                            "surface",
                            Number(polygonAreaHa.toFixed(2)),
                          )
                        }
                      >
                        {t("plot.apply_polygon_area", "Usar este valor")}
                      </Button>
                    </span>
                  ) : undefined
                }
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder={t("plot.surface")}
                  addonAfter="ha"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                label={t("plot.number_plants")}
                name="plants"
                rules={[
                  {
                    required: true,
                    message: t("plot.number_plants_required") || "El número de plantas es requerido",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder={t("plot.number_plants")}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Fila 4: Madera Intermedia / Num. Injertos (solo visible si es tipo Injerta) */}
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.plot_type !== currentValues.plot_type}>
            {({ getFieldValue }) => {
              const plotTypeId = getFieldValue('plot_type');
              const plotTypeData = plotTypeSelectProps.options?.find((opt: any) => opt.value === plotTypeId);
              const plotTypeName = String(plotTypeData?.label || '').toUpperCase();
              const isInjerta = plotTypeName.includes('INJERTA') || plotTypeName.includes('INJERTO');

              return isInjerta ? (
                <Row gutter={16}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label={t("plot.intermediate_wood")}
                      name="intermediate_wood"
                    >
                      <Select
                        {...intermediateWoodSelectProps}
                        placeholder={t("plot.intermediate_wood")}
                        showSearch
                        // Same fix as the rootstock select: filter
                        // against `label` with accent/case folding.
                        // The intermediate-wood catalogue has ~235
                        // rows, so typing is the only practical way
                        // to find an entry.
                        optionFilterProp="label"
                        filterOption={accentInsensitiveFilter}
                        allowClear
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      label={t("plot.grafting")}
                      name="grafting"
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        placeholder={t("plot.grafting")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null;
            }}
          </Form.Item>

          {/* Fila 5: Doblado con (legacy) / Es ilegal?
              El campo `doubled_with` es texto libre pre-refactor
              doblados; hoy la información vive estructurada en
              `plantings`. Sólo lo pintamos si NO hay plantings
              estructurados aún (para no confundir al admin) — el
              valor legacy se preserva en el modelo. */}
          <Row gutter={16}>
            {plantings.length < 2 ? (
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  label={t("plot.doubled_with")}
                  name="doubled_with"
                  extra="Campo legacy. Para gestionar dobladas usa la sección de abajo."
                >
                  <Input placeholder={t("plot.doubled_with")} />
                </Form.Item>
              </Col>
            ) : null}

            <Col xs={24} sm={24} md={plantings.length < 2 ? 12 : 24}>
              <Form.Item
                label={t("plot.is_ilegal")}
                name="is_ilegal"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* Fase 4 refactor de doblados (web): sección alineada con
              el wizard móvil. Toggle "Es doblada" + toggle "Total/
              Parcial" + N cards inline (uno por planting extra) +
              botón "Añadir plantación". Sólo interactivo con parcela
              persistida — en modo creación pedimos guardar primero. */}
          <Divider orientation="left">Parcela doblada</Divider>
          {!isEditMode ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Guarda la parcela primero para gestionar las plantaciones dobladas."
            />
          ) : (
            <div style={{ marginBottom: 16 }}>
              <Space wrap style={{ marginBottom: 12 }}>
                <Segmented
                  value={isDoubled ? "doubled" : "single"}
                  onChange={(v) =>
                    handleToggleDoubled(v === "doubled")
                  }
                  disabled={!canEditPlantings}
                  options={[
                    { value: "single", label: "No doblada" },
                    { value: "doubled", label: "Es doblada" },
                  ]}
                />
                {isDoubled ? (
                  <Segmented
                    value={doublingMode}
                    onChange={(v) =>
                      handleToggleDoublingMode(v as "total" | "partial")
                    }
                    disabled={!canEditPlantings}
                    options={[
                      { value: "total", label: "Total" },
                      { value: "partial", label: "Parcial" },
                    ]}
                  />
                ) : null}
              </Space>

              {isDoubled ? (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={
                      doublingMode === "total"
                        ? "Doblado total: las plantaciones comparten superficie y las plantas del padre se reparten entre ellas."
                        : "Doblado parcial: para cada plantación pulsa \"Dibujar sub-área\" y marca la porción que ocupa dentro de la parcela."
                    }
                  />
                  {extraPlantings.map((p) => (
                    <PlantingCardInline
                      key={p.id}
                      planting={p}
                      varieties={varietyOptions}
                      patterns={patternOptions}
                      plotTypes={plotTypeOptions}
                      intermediates={intermediateOptions}
                      parentGeom={geometry}
                      parentMarcoRow={watchedRow ?? null}
                      parentMarcoCol={watchedColumn ?? null}
                      parentPlants={parentPlantsForCards}
                      totalPlantings={Math.max(plantings.length, 2)}
                      isPartialMode={doublingMode === "partial"}
                      onDrawSubGeom={() => setSubGeomTarget(p)}
                      onUpdate={(patch) => updatePlanting(p.id, patch)}
                      onRemove={() => removePlanting(p.id)}
                    />
                  ))}
                  {canEditPlantings ? (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={handleAddPlanting}
                      style={{ width: "100%" }}
                    >
                      Añadir plantación
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
          )}

          {/* Production Blocks Section */}
          <Divider orientation="left">{t("plot.production_blocks") || "Bloques de Aforo"}</Divider>
          
          <div style={{ marginBottom: "16px" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="dashed"
                onClick={addProductionBlock}
                icon={<PlusOutlined />}
                style={{ width: "100%" }}
              >
                {t("plot.add_block") || "Añadir Bloque de Aforo"}
              </Button>

              {productionBlocks.length > 0 && (
                <Table
                  dataSource={productionBlocks}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: t("plot.percentage") || "Porcentaje (%)",
                      dataIndex: "percentage",
                      key: "percentage",
                      width: "25%",
                      render: (value: number, record: ProductionBlock) => (
                        <InputNumber
                          value={value}
                          min={0}
                          max={100}
                          style={{ width: "100%" }}
                          onChange={(val) => updateProductionBlock(record.key, 'percentage', val || 0)}
                          addonAfter="%"
                        />
                      ),
                    },
                    {
                      title: t("plot.avg_yield") || "Media Kg/Árbol",
                      dataIndex: "avg_yield",
                      key: "avg_yield",
                      width: "35%",
                      render: (value: number, record: ProductionBlock) => (
                        <InputNumber
                          value={value}
                          min={0}
                          step={0.1}
                          style={{ width: "100%" }}
                          onChange={(val) => updateProductionBlock(record.key, 'avg_yield', val || 0)}
                          addonAfter="kg"
                        />
                      ),
                    },
                    {
                      title: t("common.actions") || "Acciones",
                      key: "actions",
                      width: "15%",
                      align: "center" as const,
                      render: (_: any, record: ProductionBlock) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeProductionBlock(record.key)}
                        />
                      ),
                    },
                  ]}
                />
              )}

              {/* Calibre medio plot-level (mm). Un input por parcela,
                  no por bloque — reemplaza la antigua columna. */}
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t("plot.avg_caliber_plot", "Calibre medio (mm)")}
                    name="avg_caliber"
                    tooltip={t(
                      "plot.avg_caliber_plot_help",
                      "Se mide una vez para toda la parcela, no por bloque.",
                    )}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.1}
                      placeholder={t("plot.avg_caliber_placeholder", "Ej: 65")}
                      addonAfter="mm"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {productionBlocks.length > 0 && (() => {
                const totalPercentage = productionBlocks.reduce((sum, block) => sum + block.percentage, 0);
                const isInvalid = totalPercentage > 100;
                
                return (
                  <Card 
                    size="small" 
                    style={{ 
                      backgroundColor: isInvalid ? "#fff1f0" : "#f0f2f5",
                      borderColor: isInvalid ? "#ff4d4f" : undefined
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <strong>{t("plot.total_percentage") || "Porcentaje Total"}:</strong>{" "}
                        <span style={{ color: isInvalid ? "#ff4d4f" : "inherit", fontWeight: isInvalid ? "bold" : "normal" }}>
                          {totalPercentage}%
                        </span>
                        {isInvalid && (
                          <span style={{ color: "#ff4d4f", marginLeft: "8px", fontSize: "12px" }}>
                            ({t("plot.exceeds_100") || "Excede 100%"})
                          </span>
                        )}
                      </Col>
                      <Col span={12}>
                        <strong>{t("plot.estimated_production") || "Aforo Estimado"}:</strong>{" "}
                        {estimatedProduction.toFixed(2)} kg
                      </Col>
                    </Row>
                  </Card>
                );
              })()}
            </Space>
          </div>

          {/* Map section for geometry editing */}
          <div style={{ marginTop: "24px" }}>
            <h3 style={{ marginBottom: "16px" }}>
              {t("plot.map_section") || "Geometría de la Parcela"}
            </h3>
            <div 
              style={{ 
                height: "500px", 
                width: "100%",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                overflow: "hidden",
                position: "relative"
              }}
            >
              <PlotMapEditor
                geometry={geometry}
                plotName={plot?.name || ""}
                onGeometryChange={setGeometry}
                editable={true}
                referencePlots={referencePlots}
              />
            </div>
            {!geometry && (
              <div style={{ marginTop: "8px", fontSize: "12px", color: "#999" }}>
                {t("plot.map_no_geometry")}
              </div>
            )}
          </div>

          <Form.Item
            style={{ marginTop: "24px", marginBottom: 0, textAlign: "right" }}
          >
            <Space>
              <Button
                size="large"
                onClick={() => navigate(`/audits/${auditId}`)}
              >
                {t("common.cancel", "Cancelar")}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
              >
                {t("common.save")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      {/* Editor visual del sub_geom de una planting doblada — Fase C.
          Se abre desde el botón "Dibujar sub-área" de cada
          PlantingCardInline en modo parcial. */}
      {subGeomTarget ? (
        <SubGeomEditorModal
          open={!!subGeomTarget}
          parentGeom={geometry}
          planting={subGeomTarget}
          siblings={plantings}
          onCancel={() => setSubGeomTarget(null)}
          onSave={async (subGeom, surfaceHa) => {
            await updatePlanting(subGeomTarget.id, {
              sub_geom: subGeom,
              surface: surfaceHa,
            });
            setSubGeomTarget(null);
          }}
          onClear={async () => {
            await updatePlanting(subGeomTarget.id, {
              sub_geom: null,
              surface: null,
            });
            setSubGeomTarget(null);
          }}
        />
      ) : null}
    </Show>
  );
};
